/**
 * 列表页后台裁剪：与详情页 processCropImages 共用 runImageCropTaskBatch。
 *
 * T3-4.3：后台 worker 与详情「重新裁剪」行为一致——
 * submitImageCropTask（经 runImageCropTaskBatch）、onImageProgress + rememberImageCropResult、force 时清缓存。
 */
import {
  clearImageCropTaskCancelled,
  isImageCropTaskCancelled,
} from '../utils/imageCropTaskCancel';
import { resolveImageCropOriginalUrl } from '../utils/imageCropResolveUrl';
import {
  clearImageCropResultCache,
  rememberImageCropResult,
} from '../utils/imageCropResultCache';
import { runImageCropTaskBatch } from './imageCropProcessTask';
import { IMAGE_CROP_NAV_ID } from '../constants/imageCropConfig';
import { withIpeProcessingSlot } from '@/pages/ipe/workbench/utils/ipeProcessingQueue';

const activeKeys = new Set();
const cancelledKeys = new Set();

let registeredResolveOriginalUrl = null;

export function registerImageCropResolveOriginalUrl(fn) {
  registeredResolveOriginalUrl = typeof fn === 'function' ? fn : null;
}

function workerKey(taskId) {
  return `${IMAGE_CROP_NAV_ID}:${taskId}`;
}

function emitTaskProgress(task) {
  window.dispatchEvent(
    new CustomEvent('ipeTaskProgress', { detail: { taskType: IMAGE_CROP_NAV_ID, task } }),
  );
}

function isWorkerCancelled(key, taskId) {
  return cancelledKeys.has(key) || isImageCropTaskCancelled(taskId);
}

/**
 * 与 ImageCropDetailPage processCropImages 相同的整批裁剪（无 React 状态更新）。
 * @returns {Promise<{ finished: boolean, cancelled?: boolean }>}
 */
export async function runImageCropBackgroundBatch({
  taskId,
  imageIds,
  resolveOriginalUrl,
  forceReprocess = false,
  taskMeta,
  isCancelled,
  skipServerSubmit = false,
}) {
  const ids = [...new Set((imageIds || []).filter(Boolean).map(String))];
  const meta = taskMeta || { id: taskId };
  const resolve = resolveOriginalUrl || registeredResolveOriginalUrl || ((id) => resolveImageCropOriginalUrl(id));

  if (forceReprocess) {
    clearImageCropTaskCancelled(taskId);
    clearImageCropResultCache(taskId);
  }

  return runImageCropTaskBatch({
    taskId,
    taskMeta: meta,
    imageIds: ids,
    resolveOriginalUrl: resolve,
    forceReprocess,
    skipServerSubmit,
    onImageProgress: (imageId, patch) => {
      if (patch?.status === 'success' && patch?.croppedUrl) {
        rememberImageCropResult(taskId, imageId, {
          imageId,
          croppedUrl: patch.croppedUrl,
          status: 'success',
        });
      }
    },
    onTaskProgress: (patch) => {
      emitTaskProgress({
        id: taskId,
        type: IMAGE_CROP_NAV_ID,
        imageIds: ids,
        ...patch,
      });
    },
  });
}

export function isImageCropBackgroundActive(taskId) {
  return activeKeys.has(workerKey(taskId));
}

export function stopImageCropTaskInBackground(taskId) {
  const key = workerKey(taskId);
  cancelledKeys.add(key);
  activeKeys.delete(key);
}

export function resetImageCropTaskWorker(taskId) {
  const key = workerKey(taskId);
  cancelledKeys.delete(key);
  activeKeys.delete(key);
}

/** 创建任务后 / 列表续跑：执行与详情「重新裁剪」相同的整批裁剪 */
export function ensureImageCropTaskInBackground(
  taskId,
  imageIds,
  resolveOriginalUrl,
  { force = false, subAccount, taskMeta, skipServerSubmit = false } = {},
) {
  const key = workerKey(taskId);
  if (force) {
    clearImageCropTaskCancelled(taskId);
    resetImageCropTaskWorker(taskId);
  } else if (activeKeys.has(key)) {
    return;
  }
  activeKeys.add(key);
  cancelledKeys.delete(key);

  const meta = taskMeta || { id: taskId, subAccount, taskId };

  void (async () => {
    try {
      await withIpeProcessingSlot(
        taskId,
        IMAGE_CROP_NAV_ID,
        () =>
          runImageCropBackgroundBatch({
            taskId,
            imageIds,
            resolveOriginalUrl,
            forceReprocess: Boolean(force),
            taskMeta: meta,
            skipServerSubmit,
            isCancelled: () => isWorkerCancelled(key, taskId),
          }),
        { isCancelled: () => isWorkerCancelled(key, taskId) },
      );
    } catch (e) {
      console.warn('[图片裁剪-列表] 批量裁剪异常:', e);
    } finally {
      activeKeys.delete(key);
      cancelledKeys.delete(key);
    }
  })();
}

/** @deprecated 保留导出避免旧引用报错；内部已改为整批裁剪 */
export async function processImageCropTaskStep() {
  return { finished: true };
}
